import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { getBlockList, createBlock } from '../../utils/blockRegistry';
import { getIcon } from '../../utils/iconRegistry';
import { acquireModalLock } from '../../utils/modalLock';
import { useT } from '../../hooks/useT';
import styles from './BlockInserter.module.css';

const CATEGORIES = [
  { id: 'all', key: 'editor.categoryAll', fallback: 'All' },
  { id: 'text', key: 'editor.categoryText', fallback: 'Text' },
  { id: 'content', key: 'editor.categoryContent', fallback: 'Content' },
  { id: 'media', key: 'editor.categoryMedia', fallback: 'Media' },
  { id: 'layout', key: 'editor.categoryLayout', fallback: 'Layout' },
  { id: 'interactive', key: 'editor.categoryInteractive', fallback: 'Interactive' },
];

function BlockPickerModal({ onSelect, onClose }) {
  const t = useT();
  const [category, setCategory] = useState('all');
  const allBlocks = getBlockList();
  const filtered = category === 'all'
    ? allBlocks
    : allBlocks.filter(b => b.category === category);

  useEffect(() => acquireModalLock(), []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSelect = (type) => {
    const block = createBlock(type);
    if (block) {
      onSelect(block);
      onClose();
    }
  };

  // Rendered via a portal straight onto document.body: BlockInserter's own
  // trigger (`.inserter`) sits at `opacity: 0.35` so its "+" affordance is
  // discoverable on touch without a hover state, and a CSS `opacity` below 1
  // creates a new stacking context. Left as a normal child, this modal would
  // inherit that faded opacity (the whole overlay painting at 35%) AND have
  // its `z-index` scoped inside `.inserter`'s stacking context, unable to
  // rise above sibling page content — which is exactly the bug this portal
  // fixes. A modal being trapped inside whatever element happened to open it
  // is a latent bug independent of this specific opacity rule, so the portal
  // is the correct structural fix rather than a one-off workaround.
  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('editor.pickBlock', 'Add a block')}</h3>
          <button
            className={styles.modalClose}
            onClick={onClose}
            aria-label={t('editor.close', 'Close')}
            title={t('editor.close', 'Close')}
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.categories}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.categoryBtn} ${category === cat.id ? styles.categoryActive : ''}`}
              onClick={() => setCategory(cat.id)}
            >
              {t(cat.key, cat.fallback)}
            </button>
          ))}
        </div>

        <div className={styles.blockGrid}>
          {filtered.map((block) => {
            const Icon = getIcon(block.icon);
            return (
              <button
                key={block.type}
                className={styles.blockCard}
                onClick={() => handleSelect(block.type)}
              >
                {Icon && <Icon className={styles.blockCardIcon} />}
                <span className={styles.blockCardLabel}>{block.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default BlockPickerModal;
